import robustFetch from "@/utils/robustFetch";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useCookieConsent } from "../hooks/use-cookie-consent";
import "@/utils/gaDebug"; // Load GA debug tools

const IS_DEV = import.meta.env.DEV;

interface AnalyticsProps {
  gtag?: string;
  hotjar?: string;
}

/**
 * Wait for react-helmet-async to flush the updated <title> into the DOM.
 *
 * Strategy: MutationObserver on <title> fires the moment the text node
 * changes. A 600 ms fallback fires if the title stays identical between
 * pages (e.g. two pages share the same title string).
 *
 * Returns a cleanup function that cancels everything.
 */
function waitForTitleUpdate(onReady: () => void): () => void {
  let done = false;

  const finish = () => {
    if (done) return;
    done = true;
    observer.disconnect();
    clearTimeout(fallback);
    onReady();
  };

  const titleEl = document.querySelector("title");
  const observer = new MutationObserver(finish);
  if (titleEl) {
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
  }

  // Fallback: if the title text didn't change (same wording across pages),
  // fire after 600 ms so we don't silently drop the event.
  const fallback = setTimeout(finish, 600);

  return () => {
    done = true;
    observer.disconnect();
    clearTimeout(fallback);
  };
}

export default function Analytics({ gtag, hotjar }: AnalyticsProps) {
  const location = useLocation();
  const { consent } = useCookieConsent();
  const [scriptsLoaded, setScriptsLoaded] = useState(false);

  // ── Reset when consent is revoked ──────────────────────────────────────────
  useEffect(() => {
    if (!consent?.analytics && scriptsLoaded) {
      if (IS_DEV) console.log("🔄 Analytics: consent revoked");
      setScriptsLoaded(false);
    }
  }, [consent?.analytics, scriptsLoaded]);

  // ── Listen for runtime consent changes ────────────────────────────────────
  useEffect(() => {
    const handle = (e: CustomEvent) => {
      if (IS_DEV) console.log("🔔 Analytics: consent changed →", e.detail);
      if (e.detail?.analytics && !scriptsLoaded) setScriptsLoaded(false);
    };
    window.addEventListener("cookieConsentChanged", handle as EventListener);
    return () => window.removeEventListener("cookieConsentChanged", handle as EventListener);
  }, [scriptsLoaded]);

  // ── Initialise third-party scripts after consent ──────────────────────────
  useEffect(() => {
    if (!consent?.analytics) {
      if (IS_DEV) console.log("⛔ Analytics: no consent yet");
      return;
    }
    if (scriptsLoaded) return;

    if (IS_DEV) {
      if (gtag && typeof (window as any).gtag !== "undefined") {
        console.log("✅ GA4 ready | ID:", gtag, "| dataLayer length:", (window as any).dataLayer?.length ?? 0);
        console.log("   Run window.testGAEvent() to send a test event");
      } else if (gtag) {
        console.warn("⚠️ GA4 script not yet loaded — may be blocked by an ad blocker");
      }
    }

    // Hotjar — deferred, only loaded once
    if (hotjar && !document.querySelector(`script[src*="hotjar.com"]`)) {
      const s = document.createElement("script");
      s.async = true;
      s.defer = true;
      s.innerHTML = `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${hotjar},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.defer=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r)})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;
      document.head.appendChild(s);
    }

    setScriptsLoaded(true);
  }, [consent?.analytics, gtag, hotjar, scriptsLoaded]);

  // ── Page-view tracking ────────────────────────────────────────────────────
  // Fires exactly ONE page_view per navigation, AFTER Helmet has updated <title>.
  const lastTrackedPath = useRef<string>("");

  useEffect(() => {
    if (!consent?.analytics) return;

    const path = location.pathname + location.search;

    // Guard: don't re-fire for the same path (e.g. hash changes only)
    if (lastTrackedPath.current === path) return;

    // Wait for Helmet to flush the new <title> then send the event
    const cancel = waitForTitleUpdate(() => {
      lastTrackedPath.current = path;

      const pageTitle    = document.title;
      const pageLocation = window.location.href;
      const pagePath     = path;

      // ── GA4 — single event call, no gtag("config") ──────────────────────
      if (gtag && (window as any).gtag) {
        const payload = { page_title: pageTitle, page_location: pageLocation, page_path: pagePath };
        (window as any).gtag("event", "page_view", payload);
        if (IS_DEV) console.log("📊 GA4 page_view →", payload);
      }

      // ── Internal analytics ──────────────────────────────────────────────
      robustFetch("/api/analytics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "pageview",
          path: pagePath,
          title: pageTitle,
          timestamp: new Date().toISOString(),
          referrer: document.referrer,
          userAgent: navigator.userAgent,
        }),
      }).catch(() => {});
    });

    return cancel;
  }, [location, gtag, consent?.analytics]);

  return (
    <>
      {/* Google Analytics script is now in index.html <head> for better performance */}

      {/* Hotjar - deferred per .builderrules */}
      {hotjar && (
        <script
          defer
          dangerouslySetInnerHTML={{
            __html: `
              (function(h,o,t,j,a,r){
                h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                h._hjSettings={hjid:${hotjar},hjsv:6};
                a=o.getElementsByTagName('head')[0];
                r=o.createElement('script');r.async=1;r.defer=1;
                r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                a.appendChild(r);
              })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
            `,
          }}
        />
      )}
    </>
  );
}
